import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Login } from "@adaltra/ui";
import "../theme/loginAlignment.css";
import { GoogleIcon, MicrosoftIcon } from "../components/ProviderIcons";
import { RegistrationSuccess } from "../components/RegistrationSuccess";
import { PortalRegistrationForm } from "../components/PortalRegistrationForm";
import { AnimatedTitle } from "../components/AnimatedTitle";
import { LanguageMenu } from "../components/LanguageMenu";
import { authApi } from "../api/endpoints";
import { apiUrl } from "../api/client";
import { useSession } from "../hooks/useSession";
import { portalLoginCredentials } from "../utils/sharedUiIntegration.js";

const PROVIDERS = [
  { id: "google", label: "auth.googleShort", accessibleLabel: "auth.continueWithGoogle", Icon: GoogleIcon },
  { id: "microsoft", label: "auth.microsoftShort", accessibleLabel: "auth.continueWithMicrosoft", Icon: MicrosoftIcon },
];

/** Shared presentation; the Portal owns authentication, MFA, registration and destinations. */
export default function LoginPage() {
  const { t } = useTranslation();
  const { signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [authError, setAuthError] = useState(searchParams.get("authError"));
  const [registered, setRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [loginKey, setLoginKey] = useState(0);
  const afterSignIn = location.state?.from ?? "/inicio";

  useEffect(() => {
    let current = true;
    authApi.providers().then(data => {
      if (current) { setProviders(data.providers ?? []); }
    }).catch(() => { if (current) setProviders([]); });
    return () => { current = false; };
  }, []);

  async function submit(credentials) {
    setAuthError(null);
    try {
      const result = await signIn(portalLoginCredentials(credentials));
      navigate(result?.step === "2FA" ? "/verificacion-2fa" : afterSignIn,
        { replace: true, state: { from: afterSignIn } });
    } catch (error) {
      throw new Error(t(error.code ?? "errors.unexpected", { defaultValue: t("errors.unexpected") }));
    }
  }

  const providerActions = PROVIDERS.map(({ id, label, accessibleLabel, Icon }) => ({
    id, label: t(label), accessibleLabel: t(accessibleLabel), icon: <Icon />,
    disabled: !providers.some(provider => provider.id === id && provider.enabled),
    onClick: () => window.location.assign(apiUrl(`/auth/${id}/start`)),
  })).filter(provider => !provider.disabled);

  return <>
    <div className="atlas-login-alignment">
    <Login key={loginKey}
      loading={registering}
      brand={{ name: t("brand.name"), descriptor: t("brand.portal"), lettermark: true }}
      title={<AnimatedTitle text={t("auth.signInTitle")} />}
      description={t("auth.signInSubtitle")}
      descriptionColor="#8d83b1"
      formHeader={<img src="/brand/logo_adaltra_signat.svg" alt="ADALTRA — we make the connection" width={256} height={78} />}
      buttonLabel={t("auth.signIn")}
      heroTitle={<AnimatedTitle text={t("auth.heroTitle")} accent={t("auth.heroAccent")} brandMarks />}
      heroDescription={t("auth.heroSubtitle")}
      backgroundImage={{ src: "/brand/atlas-comercial-presupuestos-v1.webp" }}
      trustPoints={[t("auth.trustDocuments"), t("auth.trustReview")]}
      languageControl={<LanguageMenu />}
      error={authError ? t(authError, { defaultValue: t("errors.unexpected") }) : undefined}
      credentials={{ onSubmit: submit, onForgotPassword: () => navigate("/recuperar-contrasena"), defaultRemember: true }}
      providers={providerActions}
      registration={{
        title: <AnimatedTitle text={t("auth.registerTitle")} />,
        description: t("auth.registerSubtitle"),
        heroTitle: <AnimatedTitle text={t("auth.heroTitleRegister")} accent={t("auth.heroAccentRegister")} brandMarks />,
        heroDescription: t("auth.heroSubtitleRegister"),
        trustPoints: [t("auth.trustSecureSignUp"), t("auth.trustTracking")],
        content: <PortalRegistrationForm onRegistered={() => setRegistered(true)} onPendingChange={setRegistering} />,
      }}
      labels={{
        email: t("auth.email"), password: t("auth.password"), showPassword: t("auth.showPassword"),
        hidePassword: t("auth.hidePassword"), remember: t("auth.rememberMe"),
        forgotPassword: t("auth.forgotPassword"), pending: t("auth.signingIn"),
        unexpectedError: t("errors.unexpected"), orContinueWith: t("auth.orContinueWith"),
        secureAccess: t("auth.secureAccess"), noAccount: t("auth.noAccount"),
        createAccount: t("auth.createAccount"), haveAccount: t("auth.haveAccount"), backToLogin: t("auth.backToSignIn"),
      }}
    />
    </div>
    {registered && <RegistrationSuccess onBack={() => {
      setRegistered(false); setAuthError(null); setLoginKey(key => key + 1);
    }} />}
  </>;
}
